/**
 * MapContainer - Main Mapbox GL wrapper component
 */

import { useCallback, useRef, useEffect, useState } from 'react';
import Map, { NavigationControl, GeolocateControl, Marker, type MapRef } from 'react-map-gl';
import { useMapStore, useLocationStore } from '@/stores';
import { useNavigationStore } from '@/stores/useNavigationStore';
import { PlumeLayer } from './PlumeLayer';
import { SafeRouteLayer } from './SafeRouteLayer';
import { ThreatZoneLayer } from './ThreatZoneLayer';
import { UserLocationMarker } from './UserLocationMarker';
import { StreetSafetyOverlay } from './StreetSafetyOverlay';
import { NavigationRouteLayer } from './NavigationRouteLayer';
import { ScentMarkersLayer } from './ScentMarkersLayer';
import { RecordingRouteLayer } from './RecordingRouteLayer';
import { setMapInstance } from '@/utils/mapInstance';
import { zoomToRoute, followUser, calculateBearing } from '@/utils/cameraAnimation';
import { cn } from '@/utils/cn';
import 'mapbox-gl/dist/mapbox-gl.css';

// Mapbox token with real fallback
const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN || 'pk.eyJ1Ijoid2VubWlueWFwIiwiYSI6ImNta3g3OThuazA2ejAzZHF1M3d0eWZudnEifQ.LXK4nArE7sTpjCtTlwdFMw';

interface MapContainerProps {
  className?: string;
}

export function MapContainer({ className }: MapContainerProps) {
  const mapRef = useRef<MapRef>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [directRouteDestination, setDirectRouteDestination] = useState<{ lat: number; lng: number; name: string } | null>(null);
  const [, setRouteDebug] = useState<string>('');

  const viewport = useMapStore((state) => state.viewport);
  const setViewport = useMapStore((state) => state.setViewport);
  const setMapLoaded = useMapStore((state) => state.setMapLoaded);
  const setMapInteracting = useMapStore((state) => state.setMapInteracting);
  const layerVisibility = useMapStore((state) => state.layerVisibility);
  const plumeData = useMapStore((state) => state.plumeData);
  const activeRoute = useMapStore((state) => state.activeRoute);
  const threatZones = useMapStore((state) => state.threatZones);
  const currentStyle = useMapStore((state) => state.currentStyle);

  const currentPosition = useLocationStore((state) => state.currentPosition);
  const heading = useLocationStore((state) => state.heading);
  const isNavigating = useNavigationStore((state) => state.isNavigating);
  const activeNavRoute = useNavigationStore((state) => state.activeRoute);
  const isDemoMode = useNavigationStore((state) => state.isDemoMode);

  // Handle map load - basic setup only, route rendering is in separate effect
  const onLoad = useCallback(() => {
    setIsLoaded(true);
    setMapLoaded(true);

    const map = mapRef.current?.getMap();
    if (!map) {
      setRouteDebug('Map ref null in onLoad');
      return;
    }

    // Register global map instance for controls
    setMapInstance(map);

    // Add 3D building layer for cyberpunk effect
    try {
      const layers = map.getStyle()?.layers;
      if (layers) {
        const labelLayerId = layers.find(
          (layer) => layer.type === 'symbol' && layer.layout?.['text-field']
        )?.id;

        if (labelLayerId && !map.getLayer('3d-buildings')) {
          map.addLayer(
            {
              id: '3d-buildings',
              source: 'composite',
              'source-layer': 'building',
              filter: ['==', 'extrude', 'true'],
              type: 'fill-extrusion',
              minzoom: 14,
              paint: {
                'fill-extrusion-color': [
                  'interpolate',
                  ['linear'],
                  ['get', 'height'],
                  0, '#0a1520',
                  50, '#0f1f2e',
                  100, '#142838',
                ],
                'fill-extrusion-height': ['get', 'height'],
                'fill-extrusion-base': ['get', 'min_height'],
                'fill-extrusion-opacity': 0.8,
              },
            },
            labelLayerId
          );
        }
      }
    } catch (e) {
      console.warn('[Blurp] 3D buildings error:', e);
    }
  }, [setMapLoaded]);

  // Handle viewport changes
  const onMove = useCallback(
    (evt: { viewState: typeof viewport }) => {
      setViewport(evt.viewState);
    },
    [setViewport]
  );

  // DEFINITIVE route rendering with aggressive retry logic
  // Handles: store data, sessionStorage data, style not loaded, map ref not ready
  useEffect(() => {
    if (!isLoaded) return;

    // Get route data from Zustand store OR sessionStorage (whichever is available)
    let routeData = activeNavRoute;
    if (!routeData) {
      try {
        const pending = sessionStorage.getItem('blurp_pending_route');
        if (pending) {
          routeData = JSON.parse(pending);
          const isPreview = sessionStorage.getItem('blurp_preview_mode') === 'true';
          // Only auto-start navigation if NOT in preview mode
          if (routeData && !isPreview && !useNavigationStore.getState().isNavigating) {
            useNavigationStore.getState().startNavigation(routeData);
          }
        }
      } catch (e) {
        setRouteDebug(`Parse error: ${e}`);
      }
    }

    if (!routeData?.polyline?.length) {
      return; // No route data available - nothing to draw
    }

    const coordinates = routeData.polyline.map((p: { lat: number; lng: number }) => [p.lng, p.lat]);
    const ptCount = coordinates.length;
    let drawn = false;
    let attempts = 0;

    const tryDraw = () => {
      if (drawn) return true;
      attempts++;

      const m = mapRef.current?.getMap();
      if (!m) {
        setRouteDebug(`Waiting for map (${attempts})`);
        return false;
      }

      if (!m.isStyleLoaded()) {
        setRouteDebug(`Style loading (${attempts})`);
        return false;
      }

      try {
        // Clean up any existing route layers first (prevents "source already exists" errors)
        ['blurp-route-dash', 'blurp-route-main', 'blurp-route-glow'].forEach(id => {
          try { if (m.getLayer(id)) m.removeLayer(id); } catch {}
        });
        try { if (m.getSource('blurp-route')) m.removeSource('blurp-route'); } catch {}

        // Add route source
        m.addSource('blurp-route', {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: { type: 'LineString', coordinates },
          },
        });

        // Glow layer (wide, blurred green)
        m.addLayer({
          id: 'blurp-route-glow',
          type: 'line',
          source: 'blurp-route',
          paint: { 'line-color': '#00ff88', 'line-width': 24, 'line-blur': 12, 'line-opacity': 0.5 },
          layout: { 'line-join': 'round', 'line-cap': 'round' },
        });

        // Main route line
        m.addLayer({
          id: 'blurp-route-main',
          type: 'line',
          source: 'blurp-route',
          paint: { 'line-color': '#00ff88', 'line-width': 8, 'line-opacity': 1 },
          layout: { 'line-join': 'round', 'line-cap': 'round' },
        });

        // Dashed direction overlay
        m.addLayer({
          id: 'blurp-route-dash',
          type: 'line',
          source: 'blurp-route',
          paint: { 'line-color': '#ffffff', 'line-width': 3, 'line-dasharray': [1, 2], 'line-opacity': 0.8 },
          layout: { 'line-join': 'round', 'line-cap': 'round' },
        });

        drawn = true;
        // Keep route data in sessionStorage if in preview mode (MainLayout needs it)
        const isPreviewMode = sessionStorage.getItem('blurp_preview_mode') === 'true';
        if (!isPreviewMode) {
          sessionStorage.removeItem('blurp_pending_route');
        }

        // Show detailed debug info including first coordinate
        const firstPt = coordinates[0];
        const src = routeData === activeNavRoute ? 'store' : 'session';
        setRouteDebug(`DRAWN ${ptCount}pts [${firstPt?.[0]?.toFixed(3)},${firstPt?.[1]?.toFixed(3)}] ${src}`);

        // Set destination marker
        if (routeData.destination) {
          setDirectRouteDestination({
            lat: routeData.destination.lat,
            lng: routeData.destination.lng,
            name: routeData.destinationName || 'Destination',
          });
        }

        // Zoom to fit route (extra bottom padding in preview mode for Start Walk bar)
        const lngs = coordinates.map((c: number[]) => c[0]);
        const lats = coordinates.map((c: number[]) => c[1]);
        const previewMode = sessionStorage.getItem('blurp_preview_mode') === 'true';
        setTimeout(() => {
          try {
            m.fitBounds(
              [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
              { padding: previewMode ? { top: 80, left: 60, right: 60, bottom: 200 } : 80, duration: 1500 }
            );
          } catch {}
        }, 500);

        return true;
      } catch (err) {
        setRouteDebug(`Err ${attempts}: ${err}`);
        return false;
      }
    };

    // Try immediately
    if (tryDraw()) return;

    // Retry every 500ms for up to 20 seconds
    const interval = setInterval(() => {
      if (drawn || attempts >= 40) {
        clearInterval(interval);
        if (!drawn) setRouteDebug(`Failed after ${attempts} tries`);
        return;
      }
      tryDraw();
    }, 500);

    return () => clearInterval(interval);
  }, [isLoaded, activeNavRoute]);

  // Progressive route trimming - remove the portion already walked
  useEffect(() => {
    if (!isLoaded || !isNavigating || !currentPosition || !activeNavRoute?.polyline?.length) return;

    const m = mapRef.current?.getMap();
    if (!m) return;

    const source = m.getSource('blurp-route') as mapboxgl.GeoJSONSource | undefined;
    if (!source) return;

    // Find nearest polyline point to current position
    const polyline = activeNavRoute.polyline;
    let minDist = Infinity;
    let minIndex = 0;
    for (let i = 0; i < polyline.length; i++) {
      const dlat = currentPosition.lat - polyline[i].lat;
      const dlng = currentPosition.lng - polyline[i].lng;
      const dist = dlat * dlat + dlng * dlng;
      if (dist < minDist) {
        minDist = dist;
        minIndex = i;
      }
    }

    // Only trim if we've actually moved past some points
    if (minIndex > 0) {
      const remaining = polyline.slice(minIndex);
      const coordinates = remaining.map((p: { lat: number; lng: number }) => [p.lng, p.lat]);

      if (coordinates.length >= 2) {
        source.setData({
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates },
        });
      }
    }
  }, [isLoaded, isNavigating, currentPosition, activeNavRoute]);

  // Auto-zoom to user's start point when navigation begins (street-level, then route overview)
  useEffect(() => {
    if (!isLoaded || !isNavigating || !activeNavRoute || !mapRef.current) return;
    if (!activeNavRoute.polyline?.length) return;

    const map = mapRef.current.getMap();
    if (!map) return;

    const startPoint = activeNavRoute.polyline[0];
    // Calculate bearing toward the second point on the route for direction-aware zoom
    const nextPoint = activeNavRoute.polyline[Math.min(5, activeNavRoute.polyline.length - 1)];
    const bearing = calculateBearing(startPoint, nextPoint);

    // Step 1: Zoom into user's starting position at street level with 3D tilt
    const timer1 = setTimeout(() => {
      map.flyTo({
        center: [startPoint.lng, startPoint.lat],
        zoom: 17,
        bearing,
        pitch: 55,
        duration: 2000,
      });
    }, 300);

    // Step 2: After street-level view, briefly show full route overview
    const timer2 = setTimeout(() => {
      if (mapRef.current && activeNavRoute.polyline.length > 0) {
        zoomToRoute(mapRef.current, activeNavRoute.polyline, {
          padding: window.innerWidth < 768 ? 60 : 100,
          duration: 1500,
        });
      }
    }, 3500);

    // Step 3: Then zoom back to street-level for turn-by-turn navigation
    const timer3 = setTimeout(() => {
      map.flyTo({
        center: [startPoint.lng, startPoint.lat],
        zoom: 17,
        bearing,
        pitch: 55,
        duration: 1500,
      });
    }, 6000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [isNavigating, activeNavRoute, isLoaded]);

  // Follow user during navigation (Waze-style tracking camera)
  useEffect(() => {
    if (!isLoaded || !isNavigating || !currentPosition || isDemoMode || !mapRef.current) return;

    // Follow user with 3D tilt and bearing
    followUser(mapRef.current, currentPosition, heading ?? undefined, {
      duration: 1000,
      pitch: 50,
    });
  }, [currentPosition, heading, isNavigating, isDemoMode, isLoaded]);

  // Rotate map based on compass heading (when NOT navigating - GPS mode)
  useEffect(() => {
    if (!isLoaded || isNavigating || !mapRef.current || heading === null) return;

    const map = mapRef.current.getMap();
    // Smoothly rotate map to match phone's compass direction
    map.easeTo({
      bearing: heading,
      duration: 500,
    });
  }, [heading, isLoaded, isNavigating]);

  // Center on user location when it changes (first time only, when not navigating)
  useEffect(() => {
    if (currentPosition && mapRef.current && isLoaded && !isNavigating) {
      const map = mapRef.current.getMap();
      const currentCenter = map.getCenter();

      // Only fly to location if map is far from user
      const distance = Math.sqrt(
        Math.pow(currentCenter.lat - currentPosition.lat, 2) +
          Math.pow(currentCenter.lng - currentPosition.lng, 2)
      );

      if (distance > 0.01) {
        // More than ~1km away - keep the compass bearing
        map.flyTo({
          center: [currentPosition.lng, currentPosition.lat],
          zoom: 15,
          pitch: 45,
          bearing: heading ?? 0,
          duration: 2000,
        });
      }
    }
  }, [currentPosition, isLoaded, isNavigating, heading]);

  return (
    <div className={cn('w-full h-full [&_.mapboxgl-ctrl-logo]:hidden [&_.mapboxgl-ctrl-attrib]:hidden', className)}>
      {/* Route rendering handled by useEffect with retry logic */}
      <Map
        ref={mapRef}
        {...viewport}
        onMove={onMove}
        onLoad={onLoad}
        onDragStart={() => setMapInteracting(true)}
        onDragEnd={() => setMapInteracting(false)}
        mapboxAccessToken={MAPBOX_TOKEN}
        mapStyle={currentStyle}
        attributionControl={false}
        antialias={true}
        style={{ width: '100%', height: '100%' }}
      >
        {/* Navigation controls */}
        <NavigationControl
          position="bottom-right"
          showCompass={true}
          showZoom={true}
          visualizePitch={true}
        />

        {/* Geolocate control */}
        <GeolocateControl
          position="bottom-right"
          trackUserLocation={true}
          showUserHeading={true}
          showAccuracyCircle={false}
        />

        {/* Custom layers - rendered when map is loaded */}
        {isLoaded && (
          <>
            {/* Plume layers */}
            {layerVisibility.plumePM25 && plumeData.pm25 && (
              <PlumeLayer
                pollutantType="pm25"
                data={plumeData.pm25}
                visible={layerVisibility.plumePM25}
              />
            )}
            {layerVisibility.plumeOzone && plumeData.ozone && (
              <PlumeLayer
                pollutantType="ozone"
                data={plumeData.ozone}
                visible={layerVisibility.plumeOzone}
              />
            )}

            {/* Safe route layer */}
            {layerVisibility.safeRoute && activeRoute && (
              <SafeRouteLayer
                routeGeoJSON={activeRoute}
                safetyLevel={activeRoute.properties.safetyLevel}
              />
            )}

            {/* Threat zones */}
            {layerVisibility.threatZones &&
              threatZones.map((zone, index) => (
                <ThreatZoneLayer key={index} zone={zone} />
              ))}

            {/* User location marker */}
            {layerVisibility.userLocation && currentPosition && (
              <UserLocationMarker
                coordinates={currentPosition}
              />
            )}

            {/* Street Safety Overlay - Colors roads green/yellow/red based on NI */}
            <StreetSafetyOverlay visible={true} />

            {/* Navigation Route - Shows when navigating */}
            <NavigationRouteLayer />

            {/* Recording Route - Shows live GPS trail during activity recording */}
            <RecordingRouteLayer />

            {/* Destination marker - rendered via React, route line via raw Mapbox GL API */}
            {directRouteDestination && (
              <Marker
                longitude={directRouteDestination.lng}
                latitude={directRouteDestination.lat}
                anchor="bottom"
              >
                <div className="relative">
                  <div
                    className="absolute -inset-3 rounded-full animate-ping"
                    style={{ backgroundColor: '#00ff88', opacity: 0.3 }}
                  />
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center shadow-lg"
                    style={{ backgroundColor: '#00ff88' }}
                  >
                    <svg viewBox="0 0 24 24" fill="#000" className="w-5 h-5">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                    </svg>
                  </div>
                  <div
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-1 px-2 py-1 rounded text-xs font-hud whitespace-nowrap"
                    style={{ backgroundColor: 'rgba(0,0,0,0.8)', color: '#00ff88' }}
                  >
                    {directRouteDestination.name}
                  </div>
                </div>
              </Marker>
            )}

            {/* Scent Report Markers - Community scent reports */}
            <ScentMarkersLayer visible={true} />
          </>
        )}
      </Map>
    </div>
  );
}

export default MapContainer;
